import CustomTable, { ICustomTableColumn } from '../Table';
import SkeletonLoader from './index';

interface IClassDetailsSkeletonProps<T extends object> {
  columns: ICustomTableColumn<T>[];
}

const TeacherClassDetailsSkeleton = <T extends object>({
  columns,
}: IClassDetailsSkeletonProps<T>) => {
  return (
    <div className="flex flex-col gap-6">
      {/* Top Bar: Search and Filter Button */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4 flex-1">
          {/* Search Input Skeleton */}
          <SkeletonLoader className="h-12 w-96 rounded-lg" variant="rect" />
        </div>
        {/* Filter Button Skeleton */}
        <SkeletonLoader className="h-12 w-28 rounded-lg" variant="rect" />
      </div>

      {/* Class Overview Section */}
      <div className="flex flex-col gap-4">
        <SkeletonLoader width={180} height={22} />

        <div className="grid lg:grid-cols-[2fr_1fr] gap-6 items-start">
          {/* Lesson Completion Trend Chart */}
          <div className="bg-white rounded-xl border border-neutral-light-200 p-5 flex flex-col gap-4">
            <SkeletonLoader width={220} height={20} />
            <SkeletonLoader className="w-full h-52 rounded-lg" variant="rect" />
          </div>

          {/* Class Summary Card */}
          <div className="bg-orange-100 rounded-2xl p-6 flex flex-col justify-between gap-6 max-w-113.75 h-full">
            <div className="flex items-center gap-2">
              <SkeletonLoader
                width={34}
                height={34}
                className="rounded-lg bg-orange-200"
              />
              <SkeletonLoader
                width={180}
                height={24}
                className="bg-orange-200"
              />
            </div>
            <SkeletonLoader className="h-4 w-full rounded-lg" variant="rect" />

            <SkeletonLoader className="h-4 w-full rounded-lg" variant="rect" />

            <div className="grid grid-cols-2 gap-4">
              {[1, 2].map((item) => (
                <div
                  key={item}
                  className="bg-white rounded-lg px-4 py-2.5 flex flex-col items-center justify-center gap-1 shadow-sm"
                >
                  <SkeletonLoader width={60} height={14} />
                  <SkeletonLoader width={40} height={28} className="mt-1" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Lesson Breakdown Section */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <SkeletonLoader width={200} height={22} />
          <div className="flex items-center gap-3">
            <SkeletonLoader width={90} height={36} />
            <SkeletonLoader width={120} height={36} />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6 lg:grid-cols-4">
          {[1, 2, 3, 4].map((item) => (
            <div
              key={item}
              className="bg-white rounded-2xl p-5 border border-neutral-light-200 flex flex-col gap-4"
            >
              <div className="flex items-center gap-2">
                <SkeletonLoader
                  width={32}
                  height={32}
                  className="rounded-lg bg-neutral-200"
                />
                <SkeletonLoader width={200} height={22} />
              </div>

              <div className="flex justify-between items-center">
                <SkeletonLoader width={100} height={16} />
                <SkeletonLoader width={80} height={16} />
              </div>

              <div className="flex justify-between items-center">
                <SkeletonLoader width={80} height={16} />
                <SkeletonLoader width={80} height={16} />
              </div>

              <div className="flex flex-col gap-1.5">
                <div className="flex justify-between items-center">
                  <SkeletonLoader width={120} height={16} />
                  <SkeletonLoader width={40} height={16} />
                </div>
                <SkeletonLoader
                  height={8}
                  className="w-full rounded-full bg-neutral-200"
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Table Skeleton */}
      <CustomTable columns={columns} dataSource={[]} isLoading={true} />
    </div>
  );
};

export default TeacherClassDetailsSkeleton;
